import React from 'react';
import { ResponsiveImage } from '../components/ResponsiveImage';
import { ArrowRight, CheckCircle, Users, Target, TrendingUp, Shield } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SolutionsPage = () => {
  const solutions = [
    {
      id: 'permanent',
      title: 'Recrutement Permanent',
      description: 'Trouvez les talents qui feront la différence dans votre organisation grâce à notre expertise sectorielle et notre méthodologie éprouvée.',
      image: 'https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=800&q=80',
      features: [
        'Analyse approfondie de vos besoins',
        'Sourcing ciblé et chasse de têtes',
        'Évaluation rigoureuse des candidats',
        'Accompagnement jusqu\'à l\'intégration'
      ],
      link: '/services/recrutement-permanent'
    },
    {
      id: 'interim',
      title: 'Management de Transition',
      description: 'Bénéficiez rapidement de compétences de haut niveau pour piloter vos projets de transformation ou pallier une absence.',
      image: 'https://images.unsplash.com/photo-1664464683525-29b5d442af54?w=800&q=80',
      features: [
        'Managers expérimentés disponibles rapidement',
        'Flexibilité contractuelle',
        'Transfert de compétences',
        'Résultats mesurables'
      ],
      link: '/services/management-transition'
    },
    {
      id: 'rpo',
      title: 'RPO - Externalisation',
      description: 'Optimisez vos processus de recrutement en nous confiant tout ou partie de vos opérations de talent acquisition.',
      image: 'https://images.unsplash.com/photo-1758518730384-be3d205838e8?w=800&q=80',
      features: [
        'Solutions sur mesure',
        'Réduction des coûts de recrutement',
        'Amélioration de la qualité des embauches',
        'Reporting et analytics avancés'
      ],
      link: '/services/rpo'
    },
    {
      id: 'intelligence',
      title: 'Market Intelligence',
      description: 'Prenez des décisions éclairées grâce à nos analyses de marché et nos études de rémunération.',
      image: 'https://images.unsplash.com/photo-1573649647330-b3a1375227fe?w=800&q=80',
      features: [
        'Études de rémunération sectorielles',
        'Cartographie des talents',
        'Benchmarking concurrentiel',
        'Conseils stratégiques RH'
      ],
      link: '/services/market-intelligence'
    }
  ];

  const stats = [
    { icon: Users, value: '10,000+', label: 'Placements réussis' },
    { icon: Target, value: '95%', label: 'Taux de satisfaction' },
    { icon: TrendingUp, value: '25+', label: 'Années d\'expérience' },
    { icon: Shield, value: '100%', label: 'Confidentialité garantie' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="bg-[#E30022] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-serif mb-6">Nos solutions de recrutement</h1>
          <p className="text-xl opacity-90 max-w-3xl">
            Des solutions complètes et personnalisées pour répondre à tous vos besoins en acquisition de talents et en conseil RH.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="h-8 w-8 mx-auto mb-3 text-[#E30022]" />
                <p className="text-3xl font-bold mb-1">{stat.value}</p>
                <p className="text-gray-400 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {solutions.map((solution, index) => (
            <div 
              key={solution.id} 
              className={`grid lg:grid-cols-2 gap-12 items-center mb-20 last:mb-0 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <ResponsiveImage sizes="(min-width: 1280px) 576px, (min-width: 1024px) 50vw, 100vw"
                  src={solution.image} 
                  alt={solution.title}
                  className="rounded-lg shadow-lg w-full h-[400px] object-cover"
                />
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <h2 className="text-3xl font-serif text-gray-800 mb-4">{solution.title}</h2>
                <p className="text-gray-600 text-lg mb-6 leading-relaxed">{solution.description}</p>
                <ul className="space-y-3 mb-8">
                  {solution.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-[#E30022] flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a 
                  href={solution.link}
                  className="inline-flex items-center gap-2 bg-[#E30022] text-white px-6 py-3 rounded hover:bg-[#c7001d] transition-colors font-medium group"
                >
                  En savoir plus
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif text-gray-800 mb-4">Prêt à transformer votre recrutement ?</h2>
          <p className="text-gray-600 text-lg mb-8">
            Contactez-nous pour discuter de vos besoins et découvrir comment nous pouvons vous accompagner.
          </p>
          <a 
            href="/recrutement"
            className="inline-flex items-center gap-2 bg-[#E30022] text-white px-8 py-4 rounded hover:bg-[#c7001d] transition-colors font-medium text-lg group"
          >
            Confiez-nous vos recrutements
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SolutionsPage;
